/**
 * Security Utilities for Ottu JS SDK v3
 * 
 * Uses DOMPurify for HTML sanitization and custom validation functions
 * to prevent XSS attacks and ensure safe data handling.
 */

const DOMPurify = require('dompurify');

/**
 * Sanitizes HTML content using DOMPurify to prevent XSS attacks
 * @param {*} html - HTML content to sanitize (will be converted to string)
 * @returns {string} Sanitized HTML content
 */
const sanitizeHtml = (html) => {
  if (html === null || html === undefined) return '';
  if (typeof html !== 'string') return String(html);
  
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'br'], // Only allow safe formatting tags
    ALLOWED_ATTR: [], // No attributes allowed by default
    KEEP_CONTENT: true, // Keep text content even if tags are removed
    RETURN_DOM: false // Return string, not DOM object
  });
};

/**
 * Escapes HTML content for use in plain text contexts
 * @param {*} text - Text to escape (will be converted to string)
 * @returns {string} HTML-escaped text
 */
const escapeHtml = (text) => {
  if (text === null || text === undefined) return '';
  if (typeof text !== 'string') return String(text);
  
  return DOMPurify.sanitize(text, {
    ALLOWED_TAGS: [], // No tags allowed - pure text escaping
    ALLOWED_ATTR: [],
    KEEP_CONTENT: true
  });
};

/**
 * Validates and sanitizes URLs to prevent malicious redirects
 * @param {string} url - URL to validate
 * @returns {string} Safe URL or empty string if invalid
 */
const sanitizeUrl = (url) => {
  if (!url || typeof url !== 'string') return '';
  
  try {
    const parsed = new URL(url);
    
    // Allow HTTP for localhost addresses only
    const isLocalhost = parsed.hostname === 'localhost' || 
                       parsed.hostname === '127.0.0.1' || 
                       parsed.hostname === '[::1]' ||
                       parsed.hostname === '::1';
    
    const isHttps = parsed.protocol.toLowerCase() === 'https:';
    const isHttpLocalhost = parsed.protocol.toLowerCase() === 'http:' && isLocalhost;
    
    if (!isHttps && !isHttpLocalhost) {
      console.warn(`[Security] Blocked unsafe URL protocol: ${parsed.protocol}. Only HTTPS is allowed.`);
      return '';
    }
    
    return url;
  } catch (error) {
    console.warn(`[Security] Invalid URL blocked: ${url}`);
    return '';
  }
};

/**
 * Coerces a value to boolean, handling various input formats safely
 * @param {*} value - Value to coerce to boolean
 * @returns {boolean} Coerced boolean value
 */
const coerceToBoolean = (value) => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    const lower = value.toLowerCase().trim();
    return lower === 'true' || lower === '1' || lower === 'yes';
  }
  if (typeof value === 'number') return value !== 0;
  return false;
};

/**
 * Coerces a value to number, handling various input formats safely
 * @param {*} value - Value to coerce to number
 * @returns {number|null} Coerced number value or null if invalid
 */
const coerceToNumber = (value) => {
  if (typeof value === 'number' && !isNaN(value)) return value;
  if (typeof value === 'string') {
    const cleaned = value.replace(/[^\d.-]/g, ''); // Keep only digits, dots, and hyphens
    const parsed = parseFloat(cleaned);
    return isNaN(parsed) ? null : parsed;
  }
  return null;
};

/**
 * Expected data types for common API fields
 */
const FIELD_TYPES = {
  // Boolean fields
  'cvv_required': 'boolean',
  'is_expired': 'boolean',
  'is_preferred': 'boolean',
  'can_save_card': 'boolean',
  'is_active': 'boolean',
  'is_available': 'boolean',
  'apple_pay_available': 'boolean',
  'google_pay_available': 'boolean',
  'will_expire_soon': 'boolean',
  'use_as_jazz_sdk': 'boolean',
  
  // Array fields (preserve as-is but sanitize contents)
  'payment_methods': 'array',
  'cards': 'array',
  'payment_services': 'array',
  'flex_methods': 'array',
  'pg_icons': 'array'
};

/**
 * Checks if a field name indicates it should contain a URL
 * @param {string} fieldName - The field name to check
 * @returns {boolean} True if field appears to be URL-related
 */
const isUrlField = (fieldName) => {
  const lowerName = fieldName.toLowerCase();
  return lowerName.includes('url') || 
         lowerName.includes('redirect') ||
         lowerName.includes('href') ||
         lowerName.includes('src');
};

/**
 * Sanitizes a single field value based on its expected type and content
 * @param {string} key - The field name
 * @param {*} value - The field value
 * @returns {*} Sanitized value
 */
const sanitizeFieldValue = (key, value) => {
  if (value === null || value === undefined) {
    return value;
  }

  const expectedType = FIELD_TYPES[key];
  
  if (expectedType === 'boolean') {
    return coerceToBoolean(value);
  }
  
  if (expectedType === 'number') {
    const coerced = coerceToNumber(value);
    return coerced !== null ? coerced : null;
  }
  
  if (expectedType === 'array') {
    return Array.isArray(value) ? value.map(item => sanitizeApiData(item)) : [];
  }
  
  if (typeof value === 'string') {
    return isUrlField(key) ? sanitizeUrl(value) : escapeHtml(value);
  }
  
  if (typeof value === 'object' && value !== null) {
    return sanitizeApiData(value);
  }
  
  // For unexpected types, warn in development and return null for safety
  if (process.env.NODE_ENV === 'development') {
    console.warn(`[Security] Unexpected type for field ${key}:`, typeof value, value);
  }
  
  // For fields not in FIELD_TYPES, sanitize based on actual type
  if (typeof value === 'boolean') {
    return value; // Booleans are safe as-is
  }
  
  if (typeof value === 'number') {
    return isNaN(value) ? null : value; // Reject NaN, keep valid numbers
  }
  
  if (Array.isArray(value)) {
    return value.map(item => sanitizeApiData(item)); // Recursively sanitize array contents
  }
  
  return null;
};

/**
 * Sanitizes API data objects by escaping string values, validating URLs, and coercing types
 * @param {Object} data - Object containing API response data
 * @returns {Object} Sanitized object with escaped strings, validated URLs, and proper types
 */
const sanitizeApiData = (data) => {
  if (!data || typeof data !== 'object') return {};
  if (Array.isArray(data)) {
    return data.map(item => sanitizeApiData(item));
  }
  
  const sanitized = {};
  
  for (const [key, value] of Object.entries(data)) {
    sanitized[key] = sanitizeFieldValue(key, value);
  }
  
  return sanitized;
};

/**
 * Sanitizes specific object fields based on provided field configuration
 * @param {Object} obj - Object to sanitize
 * @param {Object} fields - Configuration object specifying how to sanitize each field
 *   - fields.escape: array of field names to HTML escape
 *   - fields.url: array of field names to URL validate
 *   - fields.preserve: array of field names to preserve as-is
 * @returns {Object} Sanitized object
 */
const sanitizeObject = (obj, fields = {}) => {
  if (!obj || typeof obj !== 'object') return obj;
  
  const { escape = [], url = [], preserve = [] } = fields;
  const sanitized = { ...obj };
  
  for (const [key, value] of Object.entries(sanitized)) {
    if (preserve.includes(key)) {
      // Keep as-is
      continue;
    } else if (url.includes(key)) {
      sanitized[key] = sanitizeUrl(value);
    } else if (escape.includes(key) && typeof value === 'string') {
      sanitized[key] = escapeHtml(value);
    }
  }
  
  return sanitized;
};

/**
 * Creates a safe error message object with escaped content
 * @param {Error|Object|string} error - Error object or message
 * @returns {Object} Safe error object with escaped message
 */
const createSafeError = (error) => {
  if (typeof error === 'string') {
    return {
      message: escapeHtml(error),
      type: 'error'
    };
  }
  
  if (error && typeof error === 'object') {
    return {
      message: escapeHtml(error.message || 'An error occurred'),
      status: error.status || 'error',
      code: error.code || undefined
    };
  }
  
  return {
    message: 'An error occurred',
    type: 'error'
  };
};

/**
 * Validates that a string contains only safe characters for HTML attributes
 * @param {string} value - Value to validate
 * @returns {boolean} True if safe for HTML attributes
 */
const isValidAttributeValue = (value) => {
  if (typeof value !== 'string') return false;
  
  // Check for dangerous characters that could break out of attributes
  const dangerousChars = /[<>"'&]/;
  return !dangerousChars.test(value);
};

/**
 * Sanitizes CSS property and value using the browser's own CSS parser
 * 
 * NOTE: This implementation deliberately defers to the browser's CSS parser
 * rather than regex-based sanitization, because CSS injection attacks are
 * sophisticated and constantly evolving. Regex approaches are prone to bypass
 * techniques like:
 * - IE expression() injections
 * - javascript: URLs in CSS url() functions  
 * - CSS keyloggers and data exfiltration
 * - Unicode escape sequences and encoding tricks
 * 
 * Validation goes through a detached element's CSSOM (setProperty), NOT through
 * a style attribute parsed out of an HTML string. Both routes hand the value to
 * the same CSS parser, but CSP applies to style attributes and not to CSSOM
 * writes - building the attribute made the browser report a style-src-attr
 * violation for every property in the merchant theme, hundreds per page load.
 * Keep validation on the CSSOM path.
 * 
 * @param {string} property - CSS property name
 * @param {string} value - CSS property value
 * @returns {string|null} Sanitized CSS value or null if invalid
 */
const sanitizeCssProperty = (property, value) => {
  if (!property || !value || typeof property !== 'string' || typeof value !== 'string') {
    return null;
  }
  
  // Restrictive allowlist for CSS properties to prevent URL-based attacks
  // Only allowing properties that cannot contain URLs or execute code
  const allowedProperties = [
    'color', 'background-color', 'border-color',
    'border-width', 'border-style', 'border-radius', 'padding', 'margin',
    'font-size', 'font-weight', 'font-family', 'font-style',
    'text-align', 'text-decoration', 'opacity',
    'width', 'height', 'max-width', 'max-height',
    'min-width', 'min-height', 'display', 'visibility',
    'flex', 'flex-direction', 'flex-wrap', 'flex-flow', 'flex-grow',
    'flex-shrink', 'flex-basis', 'justify-content', 'align-items',
    'align-self', 'align-content', 'order', 'gap', 'row-gap', 'column-gap',
    'box-shadow'
  ];
  
  const cleanProperty = property.toLowerCase().trim().replace(/[^a-z-]/g, '');
  if (!allowedProperties.includes(cleanProperty)) {
    return null;
  }
  
  try {
    // Validate through the CSSOM rather than by building and parsing a style
    // attribute. setProperty() hands the value straight to the browser's CSS
    // parser, which drops anything it cannot parse as a valid value for this
    // property - the same validation the previous HTML-parsing approach relied
    // on, minus the HTML parse. Crucially, CSP does not apply to CSSOM writes,
    // so this no longer reports a style-src-attr violation for every property.
    const probe = document.createElement('span');
    probe.style.setProperty(cleanProperty, value);

    const sanitizedValue = probe.style.getPropertyValue(cleanProperty);
    if (!sanitizedValue) {
      return null;
    }

    // The accepted value is interpolated into stylesheet text (getCssTheme in
    // util.js writes it into a <style> element), so it must not be able to end
    // the declaration, the rule, or the style element itself. No allowed
    // property has a legitimate value containing these characters, but the CSS
    // parser alone does not stop them: font-family accepts arbitrary quoted
    // strings, so `font-family: "</style><img onerror=...>"` survives parsing
    // and only breaks out later, once something round-trips the markup through
    // innerHTML. Backslashes are rejected too - CSS escapes such as \3c
    // reconstitute these characters, and engines disagree on whether
    // getPropertyValue hands them back escaped or already decoded.
    if (/[<>{};\\]/.test(sanitizedValue)) {
      return null;
    }

    return sanitizedValue;
  } catch (error) {
    // If any error occurs during sanitization, reject the value
    return null;
  }
};

/**
 * Sanitizes CSS class names to prevent injection
 * @param {string} className - CSS class name to sanitize
 * @returns {string} Sanitized class name
 */
const sanitizeCssClassName = (className) => {
  if (!className || typeof className !== 'string') return '';
  
  return escapeHtml(className).replace(/[^a-zA-Z0-9-_]/g, '');
};

/**
 * Sanitizes theme objects containing CSS properties and values
 * @param {Object} theme - Theme object with CSS properties
 * @returns {Object} Sanitized theme object
 */
const sanitizeTheme = (theme) => {
  if (!theme || typeof theme !== 'object') return {};
  
  const sanitizedTheme = {};
  
  for (const [className, properties] of Object.entries(theme)) {
    const safeClassName = sanitizeCssClassName(className);
    if (!safeClassName) continue;
    
    const sanitizedProperties = {};
    
    if (properties && typeof properties === 'object') {
      for (const [property, value] of Object.entries(properties)) {
        const colorProperties = ['background', 'border'];
        let formattedProperty = property.toLowerCase().trim();

        if (colorProperties.includes(formattedProperty)) {
          formattedProperty = `${formattedProperty}-color`;
        }
        const sanitizedValue = sanitizeCssProperty(formattedProperty, value);
        
        if (sanitizedValue) {
          // Use the cleaned property name for consistency
          const cleanProperty = formattedProperty.replace(/[^a-z-]/g, '');
          sanitizedProperties[cleanProperty] = sanitizedValue;
        }
      }
    }
    
    // Only include className if it has valid properties
    if (Object.keys(sanitizedProperties).length > 0) {
      sanitizedTheme[safeClassName] = sanitizedProperties;
    }
  }
  
  return sanitizedTheme;
};

const validateWsUrl = (url) => {
  if (!url || typeof url !== 'string') return false;
  return /^wss?:\/\/(\S+\.)?ottu\.(dev|net)/.test(url);
};

module.exports = {
  sanitizeHtml,
  escapeHtml,
  sanitizeUrl,
  sanitizeApiData,
  sanitizeObject,
  createSafeError,
  isValidAttributeValue,
  coerceToBoolean,
  coerceToNumber,
  sanitizeCssProperty,
  sanitizeCssClassName,
  sanitizeTheme,
  validateWsUrl,
};